import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Download,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  X,
  ExternalLink,
  Sparkles,
  MonitorPlay,
  Radio,
  ListChecks,
  Info,
  BellRing,
  CirclePlay,
} from 'lucide-react';
import {
  isVeloRentBrand,
  buildVeloRentGoogleAds,
  buildVeloRentMetaAds,
  buildVeloRentVerdicts,
} from '../utils/velorentAdsDemo';

const API_BASE = 'https://api.glowytics.com';

// Latency the VeloRent demo fakes so spinners and the AI check still read as real.
const DEMO_FETCH_MS = 700;
const DEMO_CHECK_MS = 1100;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Stable key for an ad — used to map AI verdicts back onto rows.
function adKey(a, platform) {
  return `${platform}:${a?.accountId || ''}:${a?.adId || ''}`;
}

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

// Keyword match type → short label.
const MATCH_ABBR = { EXACT: 'Exact', PHRASE: 'Phrase', BROAD: 'Broad' };
const MATCH_CLS = {
  EXACT: 'bg-gray-900 text-white ring-gray-900',
  PHRASE: 'bg-gray-100 text-gray-700 ring-gray-200',
  BROAD: 'bg-slate-50 text-slate-600 ring-slate-200',
};
function kwText(k) {
  if (!k) return '';
  return typeof k === 'string' ? k : (k.text || '');
}
function kwImpr(k) {
  return typeof k === 'object' && k ? Number(k.impressions30d || 0) : 0;
}
// Sort by impressions descending — keywords that actually get traffic come first.
function sortKw(keywords) {
  return (Array.isArray(keywords) ? keywords.filter((k) => kwText(k)) : [])
    .slice()
    .sort((a, b) => kwImpr(b) - kwImpr(a));
}

// Compact in-table view: top N keywords by impressions + overflow counter.
// Each chip carries an impression badge — "is this keyword really getting traffic?".
function KeywordChips({ keywords, limit = 5 }) {
  const list = sortKw(keywords);
  if (!list.length) return <span className="text-gray-300 text-[10px]">— no keywords</span>;
  const shown = list.slice(0, limit);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((k, i) => {
        const mt = typeof k === 'object' && k.matchType ? k.matchType : '';
        const impr = kwImpr(k);
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-lg bg-gray-100 text-gray-700 ring-1 ring-gray-200"
            title={`${kwText(k)}${mt ? ` · ${MATCH_ABBR[mt] || mt} match` : ''} · ${fmtInt(impr)} impressions in the last 30d`}
          >
            {kwText(k)}
            {impr > 0 && <span className="text-[8px] font-semibold text-emerald-600 tabular-nums">{fmtInt(impr)}</span>}
          </span>
        );
      })}
      {list.length > limit && (
        <span className="text-[10px] text-gray-400 self-center">+{list.length - limit}</span>
      )}
    </div>
  );
}

// Full keyword table for the detail modal: term · match type · impressions (last 30d).
// Sorted by impressions descending — keywords with no traffic (0) are dimmed.
function KeywordTable({ keywords }) {
  const list = sortKw(keywords);
  if (!list.length) return <span className="text-gray-300 text-xs">— no keywords</span>;
  const totalImpr = list.reduce((s, k) => s + kwImpr(k), 0);
  const serving = list.filter((k) => kwImpr(k) > 0).length;
  return (
    <div className="mt-2 rounded-xl ring-1 ring-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 text-[11px] text-gray-500">
        <span>{serving}/{list.length} keywords served impressions in the last 30 days</span>
        <span className="tabular-nums">{fmtInt(totalImpr)} impressions total</span>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
        {list.map((k, i) => {
          const mt = typeof k === 'object' && k.matchType ? k.matchType : '';
          const impr = kwImpr(k);
          const dead = impr === 0;
          return (
            <div key={i} className={`flex items-center gap-2 px-3 py-1.5 text-xs ${dead ? 'opacity-55' : ''}`}>
              <span className={`shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-lg ring-1 ${MATCH_CLS[mt] || 'bg-gray-50 text-gray-500 ring-gray-200'}`}>
                {MATCH_ABBR[mt] || '—'}
              </span>
              <span className="flex-1 truncate text-gray-800" title={kwText(k)}>{kwText(k)}</span>
              <span className={`shrink-0 tabular-nums text-[11px] font-semibold ${dead ? 'text-gray-400' : 'text-emerald-700'}`}>
                {fmtInt(impr)}
                <span className="text-[9px] font-normal text-gray-400 ml-0.5">impr.</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// "Is this ad actually running right now?" — an ad with an active status can still
// serve nothing because of budget or targeting. If it got impressions yesterday or
// today, it is effectively live.
// Google has no "today" (reporting is delayed; the backend derives "yesterday" from
// the latest day the API reports) — there, impressionsYesterday is the only signal.
function isDelivering(ad) {
  return Number(ad?.impressionsToday || 0) > 0 || Number(ad?.impressionsYesterday || 0) > 0;
}

// Impressions for last 7 days / yesterday / today. Answers "is this inconsistent ad
// actually live and spending money?".
function ImpressionCell({ ad }) {
  const i7 = Number(ad?.impressions7d || 0);
  const iy = Number(ad?.impressionsYesterday || 0);
  const it = Number(ad?.impressionsToday || 0);
  const live = isDelivering(ad);
  return (
    <div className="min-w-[76px] leading-tight" title={`Last 30d: ${fmtInt(ad?.impressions30d)} impr.`}>
      <div className="flex items-center gap-1.5">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : i7 > 0 ? 'bg-amber-400' : 'bg-gray-300'}`} />
        <span className="text-[11px] tabular-nums font-semibold text-gray-800">{fmtInt(i7)}</span>
        <span className="text-[9px] text-gray-400">7d</span>
      </div>
      <div className={`text-[10px] tabular-nums mt-0.5 ${live ? 'text-emerald-700 font-semibold' : 'text-gray-400'}`}>
        Today: {fmtInt(it)} · Yesterday: {fmtInt(iy)}
      </div>
    </div>
  );
}

function VerdictBadge({ v }) {
  if (!v) return <span className="text-gray-300 text-[11px]">—</span>;
  const MAP = {
    consistent: ['Consistent', 'bg-emerald-100 text-emerald-700'],
    inconsistent: ['Inconsistent', 'bg-red-100 text-red-700 ring-1 ring-red-300'],
    unknown: ['Unknown', 'bg-gray-100 text-gray-500'],
  };
  const [label, cls] = MAP[v.verdict] || MAP.unknown;
  return (
    <span
      title={v.reason || ''}
      className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-lg ${cls}`}
    >
      {label}
    </span>
  );
}

// Table cell: badge + (when inconsistent) the reason text.
function VerdictCell({ v, checking }) {
  if (!v) {
    return checking
      ? <Loader2 className="w-3 h-3 animate-spin text-gray-300" />
      : <span className="text-gray-300">—</span>;
  }
  return (
    <div className="max-w-[220px]">
      <VerdictBadge v={v} />
      {v.verdict !== 'consistent' && v.reason && (
        <div
          className={`mt-1 text-[10px] leading-snug ${v.verdict === 'inconsistent' ? 'text-red-600' : 'text-gray-400'}`}
          title={v.reason}
        >
          {v.reason}
        </div>
      )}
    </div>
  );
}

function getBrandId() {
  try {
    return (
      localStorage.getItem('currentBrandId') ||
      localStorage.getItem('selectedBrandId') ||
      sessionStorage.getItem('currentBrandId') ||
      sessionStorage.getItem('selectedBrandId') ||
      ''
    );
  } catch (_) {
    return '';
  }
}

function joinList(arr) {
  if (!Array.isArray(arr)) return '';
  return arr.filter(Boolean).join(' | ');
}

function maxLen(ads, key) {
  let m = 0;
  for (const a of ads) {
    const arr = a?.[key];
    if (Array.isArray(arr) && arr.length > m) m = arr.length;
  }
  return m;
}

// Per-campaign top-spending RSA filter for the export.
// - Keeps only RESPONSIVE_SEARCH_AD rows
// - Groups by accountId + campaignId (campaigns can repeat across accounts)
// - Picks the single ad with the highest cost30d per group
function topRsaPerCampaign(ads) {
  const best = new Map();
  for (const a of ads) {
    if (a.adType !== 'RESPONSIVE_SEARCH_AD') continue;
    const key = `${a.accountId || ''}::${a.campaignId || ''}`;
    const cost = Number(a.cost30d || 0);
    const cur = best.get(key);
    if (!cur || cost > Number(cur.cost30d || 0)) {
      best.set(key, a);
    }
  }
  return Array.from(best.values());
}

// Per-campaign top-spending Meta ad. Mirror of the Google logic above —
// pick the highest spend30d ad in each (account, campaign) group.
function topAdPerMetaCampaign(ads) {
  const best = new Map();
  for (const a of ads) {
    const key = `${a.accountId || ''}::${a.campaignId || ''}`;
    const spend = Number(a.spend30d || 0);
    const cur = best.get(key);
    if (!cur || spend > Number(cur.spend30d || 0)) {
      best.set(key, a);
    }
  }
  return Array.from(best.values());
}

function googleRowsForExcel(ads) {
  const maxH = maxLen(ads, 'headlines');
  const maxD = maxLen(ads, 'descriptions');
  return ads.map((a) => {
    const row = {
      Account: a.accountName || a.accountId || '',
      'Campaign ID': a.campaignId || '',
      'Campaign Name': a.campaignName || '',
      'Ad Group ID': a.adGroupId || '',
      'Ad Group Name': a.adGroupName || '',
      'Targeted Keywords': (a.keywords || []).map((k) => (typeof k === 'string' ? k : k.text)).filter(Boolean).join(' | '),
      'Ad ID': a.adId || '',
      'Ad Name': a.adName || '',
      'Ad Type': a.adType || '',
      Status: a.status || '',
      'Cost (Last 30d)': Number(a.cost30d || 0),
      'Impressions (Last 30d)': Number(a.impressions30d || 0),
      'Impressions (Last 7d)': Number(a.impressions7d || 0),
      'Impressions (Yesterday)': Number(a.impressionsYesterday || 0),
      'Clicks (Last 30d)': Number(a.clicks30d || 0),
      'Final URL': joinList(a.finalUrls),
    };
    for (let i = 0; i < maxH; i++) {
      row[`Headline ${i + 1}`] = (a.headlines || [])[i] || '';
    }
    for (let i = 0; i < maxD; i++) {
      row[`Description ${i + 1}`] = (a.descriptions || [])[i] || '';
    }
    row['Long Headline'] = a.longHeadline || '';
    row['Path 1'] = a.path1 || '';
    row['Path 2'] = a.path2 || '';
    row['Business Name'] = a.businessName || '';
    row['Phone Number'] = a.phoneNumber || '';
    row['Image URL'] = joinList(a.images);
    row['All Texts'] = joinList(a.rawTexts);
    return row;
  });
}

function metaRowsForExcel(ads) {
  const maxT = maxLen(ads, 'titles');
  const maxB = maxLen(ads, 'bodies');
  const maxDsc = maxLen(ads, 'descriptions');
  return ads.map((a) => {
    const row = {
      Account: a.accountName || a.accountId || '',
      'Campaign ID': a.campaignId || '',
      'Campaign Name': a.campaignName || '',
      'Ad Set ID': a.adsetId || '',
      'Ad Set Name': a.adsetName || '',
      'Ad ID': a.adId || '',
      'Ad Name': a.adName || '',
      Status: a.status || '',
      'Effective Status': a.effectiveStatus || '',
      'Spend (Last 30d)': Number(a.spend30d || 0),
      'Impressions (Last 30d)': Number(a.impressions30d || 0),
      'Impressions (Last 7d)': Number(a.impressions7d || 0),
      'Impressions (Yesterday)': Number(a.impressionsYesterday || 0),
      'Creative ID': a.creativeId || '',
      'Creative Name': a.creativeName || '',
      Title: a.title || '',
      Body: a.body || '',
      Description: a.description || '',
      'Link URL': a.linkUrl || '',
      CTA: a.callToAction || '',
      'Image URL': a.imageUrl || '',
      'Thumbnail URL': a.thumbnailUrl || '',
      'Catalog Images': joinList(a.images),
    };
    for (let i = 0; i < maxT; i++) {
      row[`AFS Title ${i + 1}`] = (a.titles || [])[i] || '';
    }
    for (let i = 0; i < maxB; i++) {
      row[`AFS Body ${i + 1}`] = (a.bodies || [])[i] || '';
    }
    for (let i = 0; i < maxDsc; i++) {
      row[`AFS Description ${i + 1}`] = (a.descriptions || [])[i] || '';
    }
    row['All Texts'] = joinList(a.rawTexts);
    row['Created Time'] = a.createdTime || '';
    return row;
  });
}

const STATUS_PILL = {
  ENABLED: 'bg-emerald-100 text-emerald-700',
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  REMOVED: 'bg-gray-200 text-gray-600',
  DELETED: 'bg-gray-200 text-gray-600',
  ARCHIVED: 'bg-gray-200 text-gray-600',
  DISAPPROVED: 'bg-red-100 text-red-700',
  WITH_ISSUES: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }) {
  const cls = STATUS_PILL[status] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-lg ${cls}`}>
      {status || '—'}
    </span>
  );
}

export default function AdsLibraryExport() {
  const brandId = useMemo(getBrandId, []);
  const [tab, setTab] = useState('google'); // 'google' | 'meta'

  const [googleLoading, setGoogleLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [googleData, setGoogleData] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [googleError, setGoogleError] = useState('');
  const [metaError, setMetaError] = useState('');

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // { ad, platform }
  const [verdicts, setVerdicts] = useState({}); // { adKey: { verdict, reason } }
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [onlyInconsistent, setOnlyInconsistent] = useState(false);
  // Google tab. Off (default): active campaigns plus the paused/disabled ad groups
  // and ads inside them. On: only fully active ads (everything ENABLED).
  const [fullyActiveOnly, setFullyActiveOnly] = useState(false);
  // Google tab, delivery filter. Same logic as Meta: even under active campaigns
  // most ads are not actually running (Skechers: ~106 of 3,360 delivering).
  const [googleDeliveringOnly, setGoogleDeliveringOnly] = useState(true);
  // Meta tab. Hundreds of ads paused years ago pile up under active campaigns
  // (Skechers: only ~76 of 768 ads actually delivering). Delivering ones are the
  // default; turn it off to list every ad in the active campaigns.
  const [metaDeliveringOnly, setMetaDeliveringOnly] = useState(true);
  const inFlight = useRef(false);
  // VeloRent is a fabricated sales-demo brand with no ad accounts connected, so
  // the page serves static content instead of hitting the live endpoints.
  const isDemo = useMemo(() => isVeloRentBrand(brandId), [brandId]);

  const fetchGoogle = async () => {
    if (!brandId) { setGoogleError('No brand selected'); return; }
    setGoogleLoading(true);
    setGoogleError('');
    if (isDemo) {
      await sleep(DEMO_FETCH_MS);
      setGoogleData(buildVeloRentGoogleAds());
      setGoogleLoading(false);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/api/labs/google-ads/ads-live`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ brandId }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setGoogleData(json);
    } catch (e) {
      setGoogleError(e?.message || 'Could not load Google Ads data');
    } finally {
      setGoogleLoading(false);
    }
  };

  const fetchMeta = async () => {
    if (!brandId) { setMetaError('No brand selected'); return; }
    setMetaLoading(true);
    setMetaError('');
    if (isDemo) {
      await sleep(DEMO_FETCH_MS);
      setMetaData(buildVeloRentMetaAds());
      setMetaLoading(false);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/api/labs/meta-ads/ads-live`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ brandId }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setMetaData(json);
    } catch (e) {
      setMetaError(e?.message || 'Could not load Meta data');
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    if (!brandId || inFlight.current) return;
    inFlight.current = true;
    // Parallel: both kick off on mount per user request.
    Promise.all([fetchGoogle(), fetchMeta()]).finally(() => { inFlight.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const refreshAll = () => {
    if (googleLoading || metaLoading) return;
    fetchGoogle();
    fetchMeta();
  };

  // AI consistency check — checks every ad on the active tab.
  const runConsistencyCheck = async () => {
    const isG = tab === 'google';
    // Demo brand: verdicts ship with the static data, so every visible row gets
    // one instead of calling the model.
    if (isDemo) {
      const shown = isG ? filteredGoogle : filteredMeta;
      if (!shown.length || checkLoading) return;
      setCheckLoading(true);
      setCheckError('');
      await sleep(DEMO_CHECK_MS);
      const results = buildVeloRentVerdicts(shown, (a) => adKey(a, isG ? 'google' : 'meta'));
      setVerdicts((prev) => {
        const next = { ...prev };
        results.forEach((r) => { next[r.id] = { verdict: r.verdict, reason: r.reason }; });
        return next;
      });
      setCheckLoading(false);
      return;
    }
    let list = isG ? (googleData?.ads || []) : (metaData?.ads || []);
    // Google: only consider ENABLED Responsive Search Ads inside an ENABLED ad
    // group (paused/removed at any level is skipped).
    if (isG) {
      list = list.filter((a) => a.status === 'ENABLED' && a.adGroupStatus === 'ENABLED'
        && a.campaignStatus !== 'PAUSED' && a.campaignStatus !== 'REMOVED'
        && a.adType === 'RESPONSIVE_SEARCH_AD');
      // Apply the on-screen delivery filter here too, so the AI does not burn the
      // 500-ad quota on ads that are not actually running.
      if (googleDeliveringOnly) list = list.filter(isDelivering);
    } else {
      // Meta: the backend already returns active campaigns only; apply the
      // on-screen delivery filter here as well so the AI does not burn the
      // 500-ad quota on ads paused years ago.
      if (metaDeliveringOnly) list = list.filter(isDelivering);
    }
    if (checkLoading) return;
    // Sessizce cikmak yerine SEBEBINI soyle: aksi halde dugmeye basan kullanici
    // "kontrol edildi" saniyor.
    if (!list.length) {
      setCheckError(isG
        ? 'No eligible Google ad to check: only ENABLED responsive search ads inside an active campaign are checked (and, with "Delivering only" ticked, they must have served impressions).'
        : 'No eligible Meta ad to check on this tab with the current filters.');
      return;
    }
    setCheckLoading(true);
    setCheckError('');
    try {
      const payload = list.map((a) => ({
        id: adKey(a, isG ? 'google' : 'meta'),
        campaignName: a.campaignName || '',
        adGroupName: isG ? (a.adGroupName || '') : (a.adsetName || ''),
        adName: a.adName || '',
        // Reklamverenin adi — cok markali perakendecide metinde MAGAZA adi da
        // gecer ("ASICS Modelleri KLAUD'da"); bu ikinci bir marka degil.
        accountName: a.accountName || '',
        texts: (a.rawTexts && a.rawTexts.length)
          ? a.rawTexts
          : [...(a.headlines || []), ...(a.descriptions || []), a.title, a.body, a.longHeadline].filter(Boolean),
        finalUrls: isG ? (a.finalUrls || []) : (a.linkUrl ? [a.linkUrl] : []),
        // Targeted keywords of the Google ad group — so the AI can also judge
        // whether the ad copy contradicts the search intent being targeted.
        // ONLY keywords that served impressions in the last 30 days are sent:
        // a zero-impression keyword spends nothing and must not flag anything.
        keywords: isG
          ? (a.keywords || [])
              .filter((k) => typeof k === 'object' && Number(k.impressions30d || 0) > 0)
              .map((k) => k.text)
              .filter(Boolean)
          : [],
      }));
      const resp = await fetch(`${API_BASE}/api/labs/ads-live/consistency-check`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ ads: payload }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setVerdicts((prev) => {
        const next = { ...prev };
        (json.results || []).forEach((r) => { next[r.id] = { verdict: r.verdict, reason: r.reason }; });
        return next;
      });
    } catch (e) {
      setCheckError(e?.message || 'AI check failed');
    } finally {
      setCheckLoading(false);
    }
  };

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const gAds = googleData?.ads || [];
    const mAds = metaData?.ads || [];

    // Per user spec: Google Ads sheet only contains the top-spending RSA per
    // campaign over the last 30 days. Non-RSA ad types (Shopping, Display, etc.)
    // and lower-spend RSAs are excluded from the export.
    const gAdsForExport = topRsaPerCampaign(gAds).sort(
      (x, y) => Number(y.cost30d || 0) - Number(x.cost30d || 0),
    );

    if (gAdsForExport.length) {
      const wsG = XLSX.utils.json_to_sheet(googleRowsForExcel(gAdsForExport));
      XLSX.utils.book_append_sheet(wb, wsG, 'Google Ads (Top RSA per Camp.)');
    }

    // Mirror the Google logic: only the top-spending Meta ad per campaign.
    const mAdsForExport = topAdPerMetaCampaign(mAds).sort(
      (x, y) => Number(y.spend30d || 0) - Number(x.spend30d || 0),
    );
    if (mAdsForExport.length) {
      const wsM = XLSX.utils.json_to_sheet(metaRowsForExcel(mAdsForExport));
      XLSX.utils.book_append_sheet(wb, wsM, 'Meta Ads (Top per Campaign)');
    }
    if (!wb.SheetNames.length) {
      const wsEmpty = XLSX.utils.aoa_to_sheet([['No ads could be fetched for this brand.']]);
      XLSX.utils.book_append_sheet(wb, wsEmpty, 'Info');
    }
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    XLSX.writeFile(wb, `glowytics_ads_${stamp}.xlsx`);
  };

  // Filtered view (search box)
  const filterText = search.trim().toLowerCase();
  const filteredGoogle = useMemo(() => {
    // The backend now only fetches ads from ENABLED campaigns (including the
    // paused/removed ad groups and ads inside them), so there is no need to
    // filter on campaign status here.
    // fullyActiveOnly: ad group and ad must be ENABLED too.
    // googleDeliveringOnly: ads that served impressions yesterday, i.e. really live.
    let list = googleData?.ads || [];
    if (fullyActiveOnly) {
      list = list.filter((a) => a.adGroupStatus === 'ENABLED' && a.status === 'ENABLED');
    }
    if (googleDeliveringOnly) list = list.filter(isDelivering);
    if (!filterText) return list;
    return list.filter((a) => {
      const hay = [
        a.adName, a.campaignName, a.adGroupName, a.status, a.adType, a.adId,
        a.accountName, joinList(a.headlines), joinList(a.descriptions),
        a.longHeadline, joinList(a.finalUrls), a.businessName,
      ].join(' ').toLowerCase();
      return hay.includes(filterText);
    });
  }, [googleData, filterText, fullyActiveOnly, googleDeliveringOnly]);

  const filteredMeta = useMemo(() => {
    // In "campaign_active" mode the backend only returns ads from campaigns that
    // are genuinely active (including their paused ad sets and ads), so no extra
    // status filtering is needed here — the only question is "is it delivering".
    const list = metaDeliveringOnly
      ? (metaData?.ads || []).filter(isDelivering)
      : (metaData?.ads || []);
    if (!filterText) return list;
    return list.filter((a) => {
      const hay = [
        a.adName, a.campaignName, a.adsetName, a.status, a.effectiveStatus,
        a.adId, a.accountName, a.title, a.body, a.description,
        a.linkUrl, a.callToAction, joinList(a.titles), joinList(a.bodies),
      ].join(' ').toLowerCase();
      return hay.includes(filterText);
    });
  }, [metaData, filterText, metaDeliveringOnly]);

  const totalGoogle = googleData?.total ?? 0;
  const totalMeta = metaData?.total ?? 0;
  const anyLoading = googleLoading || metaLoading;
  const exportDisabled = anyLoading || (!totalGoogle && !totalMeta);

  const isG = tab === 'google';
  const activeList = isG ? filteredGoogle : filteredMeta;
  const activeTotal = isG ? totalGoogle : totalMeta;
  const activePlatform = isG ? 'google' : 'meta';
  const deliveringCount = activeList.filter(isDelivering).length;
  const checkedList = activeList.filter((a) => verdicts[adKey(a, activePlatform)]);
  const inconsistentCount = checkedList.filter(
    (a) => verdicts[adKey(a, activePlatform)].verdict === 'inconsistent',
  ).length;
  // "unknown" = AI karar veremedi VEYA cagri basarisiz oldu. Ayrica sayilmazsa
  // basarisiz bir kontrol ekranda "tutarsizlik yok" gibi gorunuyor.
  const unknownCount = checkedList.filter(
    (a) => verdicts[adKey(a, activePlatform)].verdict === 'unknown',
  ).length;

  // "Inconsistent only" filter — shows rows whose verdict is inconsistent.
  const shownGoogle = onlyInconsistent
    ? filteredGoogle.filter((a) => verdicts[adKey(a, 'google')]?.verdict === 'inconsistent')
    : filteredGoogle;
  const shownMeta = onlyInconsistent
    ? filteredMeta.filter((a) => verdicts[adKey(a, 'meta')]?.verdict === 'inconsistent')
    : filteredMeta;

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff] min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gray-900 rounded-xl">
            <MonitorPlay className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Ads Content (Live)</h1>
            <p className="text-sm text-gray-500 mt-1">
              Live ad copy and creatives from active Google Ads &amp; Meta campaigns
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={runConsistencyCheck}
            disabled={anyLoading || checkLoading || (isG ? !totalGoogle : !totalMeta)}
            title="Compares ad copy against the campaign / ad group name using AI"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl bg-gray-900 hover:bg-black text-white shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {checkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Consistency Check
          </button>
          <button
            onClick={refreshAll}
            disabled={anyLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${anyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={downloadExcel}
            disabled={exportDisabled}
            title="Download as Excel"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl bg-gray-900 hover:bg-black text-white shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export Excel ({totalGoogle + totalMeta})
          </button>
        </div>
      </div>

      {/* Automatic 4-hourly monitoring — opt-in, off by default */}
      <MonitorCard brandId={brandId} />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard
          icon={<PlatformIcon platform={activePlatform} className="w-5 h-5" />}
          tone="gray"
          label={isG ? 'Google Ads — shown' : 'Meta Ads — shown'}
          value={fmtInt(activeList.length)}
          sub={`${fmtInt(activeTotal)} fetched in total`}
        />
        <SummaryCard
          icon={<Radio className="w-5 h-5 text-gray-900" />}
          tone="gray"
          label="Delivering"
          value={fmtInt(deliveringCount)}
          sub="impressions yesterday / today"
        />
        <SummaryCard
          icon={<ListChecks className="w-5 h-5 text-gray-900" />}
          tone="gray"
          label="AI checked"
          value={fmtInt(checkedList.length)}
          sub={checkedList.length ? 'on the current tab' : 'not run yet'}
        />
        <SummaryCard
          icon={<AlertCircle className={`w-5 h-5 ${inconsistentCount ? 'text-red-600' : 'text-gray-400'}`} />}
          tone={inconsistentCount ? 'red' : 'gray'}
          label="Inconsistent"
          value={fmtInt(inconsistentCount)}
          sub={checkedList.length ? `of ${fmtInt(checkedList.length)} checked ads` : '—'}
        />
      </div>

      {/* Toolbar: tabs + filters + search */}
      <div className="bg-white p-4 rounded-2xl shadow-sm flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
          <TabButton
            active={isG}
            onClick={() => setTab('google')}
            loading={googleLoading}
            label="Google Ads"
            platform="google"
            count={`${filteredGoogle.length}/${totalGoogle}`}
          />
          <TabButton
            active={!isG}
            onClick={() => setTab('meta')}
            loading={metaLoading}
            label="Meta Ads"
            platform="meta"
            count={`${filteredMeta.length}/${totalMeta}`}
          />
        </div>

        {isG ? (
          <div className="flex flex-wrap items-center gap-3">
            <CheckboxFilter
              checked={googleDeliveringOnly}
              onChange={setGoogleDeliveringOnly}
              label="Delivering only (yesterday)"
              title="Checked: only ads that served impressions yesterday, i.e. really live. Unchecked: every ad of the active campaigns. (Google reporting is delayed, so there is no 'today' data.)"
            />
            <CheckboxFilter
              checked={fullyActiveOnly}
              onChange={setFullyActiveOnly}
              label="Fully active ads only"
              title="Checked: only fully live ads (campaign + ad group + ad all active). Unchecked: active campaigns plus the paused/disabled ad groups and ads inside them."
            />
          </div>
        ) : (
          <CheckboxFilter
            checked={metaDeliveringOnly}
            onChange={setMetaDeliveringOnly}
            label="Delivering only (yesterday / today)"
            title="Checked: only ads that served impressions yesterday or today, i.e. really live. Unchecked: every ad of the active campaigns (including the paused ones inside them)."
          />
        )}

        <div className="relative w-full sm:w-auto sm:ml-auto sm:max-w-xs sm:flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ad name, copy, campaign…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </div>

      {/* Account summary */}
      {isG && googleData?.accounts?.length > 0 && (
        <AccountSummary accounts={googleData.accounts} platform="google" />
      )}
      {!isG && metaData?.accounts?.length > 0 && (
        <AccountSummary accounts={metaData.accounts} platform="meta" />
      )}

      {/* Errors */}
      {isG && googleError && (
        <ErrorBanner text={googleError} onRetry={fetchGoogle} loading={googleLoading} />
      )}
      {!isG && metaError && (
        <ErrorBanner text={metaError} onRetry={fetchMeta} loading={metaLoading} />
      )}

      {/* AI check error / summary */}
      {checkError && (
        <ErrorBanner text={checkError} onRetry={runConsistencyCheck} loading={checkLoading} />
      )}
      {checkedList.length > 0 && (
        <div className="bg-white px-5 py-3 rounded-2xl shadow-sm text-xs flex flex-wrap items-center gap-3">
          <span className={`px-2.5 py-1 rounded-lg font-semibold ${inconsistentCount ? 'bg-red-100 text-red-700' : (unknownCount === checkedList.length ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')}`}>
            {inconsistentCount
              ? `${inconsistentCount} inconsistent ad${inconsistentCount === 1 ? '' : 's'} found`
              : (unknownCount === checkedList.length ? 'No verdict — the AI could not judge any ad' : 'No inconsistent ads')}
          </span>
          <span className="text-gray-500">{checkedList.length} ads checked with AI</span>
          {unknownCount > 0 && (
            <span className="text-amber-700">
              {unknownCount} undecided — hover the badge in the table for the reason
            </span>
          )}
          {(inconsistentCount > 0 || onlyInconsistent) && (
            <button
              onClick={() => setOnlyInconsistent((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-semibold shadow-sm transition-colors ${onlyInconsistent ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white text-red-700 border border-red-200 hover:bg-red-50'}`}
            >
              <AlertCircle className="w-3.5 h-3.5" />
              {onlyInconsistent ? 'Show all' : 'Show inconsistent only'}
            </button>
          )}
        </div>
      )}

      {/* Tables */}
      {isG && (
        <GoogleTable ads={shownGoogle} loading={googleLoading} verdicts={verdicts} checking={checkLoading} onRowClick={(ad) => setSelected({ ad, platform: 'google' })} />
      )}
      {!isG && (
        <MetaTable ads={shownMeta} loading={metaLoading} verdicts={verdicts} checking={checkLoading} onRowClick={(ad) => setSelected({ ad, platform: 'meta' })} />
      )}

      {/* How this page works — reference notes, kept at the bottom */}
      <div className="bg-white px-5 py-4 rounded-2xl shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center">
            <Info className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <div className="space-y-1.5 text-xs text-gray-500 leading-relaxed max-w-4xl">
            <p>
              Both tabs fetch <b className="text-gray-700">active (running) campaigns</b> only — including the paused
              ad groups and ads inside them. By default only <b className="text-gray-700">actually delivering</b> ads
              (with impressions yesterday or today) are listed; clear the <b className="text-gray-700">"Delivering only"</b>
              {' '}checkbox to also see the paused ads of active campaigns. The export writes two sheets to Excel.
            </p>
            <p>
              Google Ads only returns "REMOVED" ads for roughly the last 30–90 days. Older deleted ads are not
              available from the API.
            </p>
            <p>
              Excel — Google Ads sheet: only the <b className="text-gray-700">highest-spending RSA of the last 30 days</b>
              {' '}per campaign is exported. The table view keeps showing every ad.
            </p>
          </div>
        </div>
      </div>

      {selected && (
        <AdDetailModal
          ad={selected.ad}
          platform={selected.platform}
          verdict={verdicts[adKey(selected.ad, selected.platform)]}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/**
 * Automatic monitoring card.
 *
 * Backend runs the same AI consistency check every 4 hours and emails ONLY the
 * newly-found inconsistencies to the brand owners. Opt-in: until someone flips
 * this switch there is no config row and the cron skips the brand entirely.
 */
function MonitorCard({ brandId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [openDetails, setOpenDetails] = useState(false);

  const call = async (path, options = {}) => {
    const resp = await fetch(`${API_BASE}/api/labs/ads-live/${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      ...options,
    });
    const json = await resp.json();
    if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
    return json;
  };

  useEffect(() => {
    if (!brandId) { setLoading(false); return; }
    let alive = true;
    call('monitor-config')
      .then((j) => { if (alive) setState(j); })
      .catch((e) => { if (alive) setError(e?.message || 'Could not load monitor settings'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const toggle = async () => {
    if (saving || !state) return;
    setSaving(true);
    setError('');
    setNote('');
    try {
      const next = await call('monitor-config', {
        method: 'PUT',
        body: JSON.stringify({ enabled: !state.enabled }),
      });
      setState(next);
      setNote(next.enabled
        ? 'Monitoring on — the next check runs within 4 hours.'
        : 'Monitoring off — no automatic checks or emails.');
    } catch (e) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    setError('');
    setNote('');
    try {
      const r = await call('monitor-run-now', { method: 'POST', body: '{}' });
      setNote(r.willEmail
        ? 'Check started. Owners get an email if a new inconsistency is found. Fetching live ads takes a few minutes.'
        : 'Check started. Monitoring is off, so findings are recorded but no email is sent.');
    } catch (e) {
      setError(e?.message || 'Could not start the check');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-5 rounded-2xl shadow-sm flex items-center gap-3 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading monitor settings…
      </div>
    );
  }
  if (!state) {
    return (
      <div className="bg-white p-5 rounded-2xl shadow-sm text-sm text-gray-500">
        {error || 'Monitor settings unavailable.'}
      </div>
    );
  }

  const on = state.enabled;
  const recipients = state.effectiveRecipients || [];
  const last = state.lastRun;
  const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

  // Ayrıntılar (alıcılar, son tur, açık bulgu) tek satırlık özete sığmıyor ama
  // her zaman görünmeleri de gerekmiyor — title'da ve "Details" açılırında.
  const summaryBits = [
    `${fmtInt(state.openFindings)} open`,
    state.lastRunAt ? `last ${fmtWhen(state.lastRunAt)}` : 'never run',
  ].join(' · ');

  return (
    <div className="bg-white px-5 py-3 rounded-2xl shadow-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <BellRing className={`w-4 h-4 shrink-0 ${on ? 'text-emerald-600' : 'text-gray-400'}`} />
        <span className="text-sm font-semibold text-gray-800">Automatic monitoring</span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-lg ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {on ? 'ON' : 'OFF'}
        </span>
        <span
          className="text-xs text-gray-500"
          title="Every 4 hours the AI re-checks all live ad content. Only newly appearing inconsistencies are emailed — repeats are never re-sent."
        >
          Every 4h · emails brand owners on new inconsistencies
        </span>
        <span className="text-xs text-gray-400 hidden sm:inline">· {summaryBits}</span>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setOpenDetails((v) => !v)}
            className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2"
          >
            {openDetails ? 'Hide' : 'Details'}
          </button>
          <button
            onClick={runNow}
            disabled={running}
            title="Run one check right now"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CirclePlay className="w-3.5 h-3.5" />}
            Run now
          </button>
          <button
            onClick={toggle}
            disabled={saving}
            role="switch"
            aria-checked={on}
            aria-label="Automatic monitoring"
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-emerald-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {openDetails && (
        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="col-span-2 md:col-span-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Recipients</div>
            <div className="text-gray-700 truncate" title={recipients.join(', ')}>
              {recipients.length ? recipients.join(', ') : 'No brand owner email found'}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Last check</div>
            <div className="text-gray-700">{fmtWhen(state.lastRunAt)}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Open findings</div>
            <div className={`font-semibold tabular-nums ${state.openFindings ? 'text-red-600' : 'text-gray-700'}`}>
              {fmtInt(state.openFindings)}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Last email</div>
            <div className="text-gray-700">{fmtWhen(state.lastAlertAt)}</div>
          </div>
          {last && (
            <p className="col-span-2 md:col-span-4 text-[11px] text-gray-400">
              Last run ({last.trigger}) · {last.status} · {fmtInt(last.checkedCount)} ads checked ·{' '}
              {fmtInt(last.inconsistent)} inconsistent ({fmtInt(last.newFindings)} new)
              {last.note ? ` · ${last.note}` : ''}
            </p>
          )}
        </div>
      )}

      {note && <p className="mt-2 text-xs text-emerald-700">{note}</p>}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// Kart ikon zeminleri nötr; renk SADECE durum bildiren yerlerde kalıyor
// (kırmızı = tutarsız/hata, yeşil = tutarlı/yayında, amber = uyarı).
const TONE_BG = {
  emerald: 'bg-emerald-50',
  red: 'bg-red-50',
  gray: 'bg-gray-100',
};

function SummaryCard({ icon, tone = 'gray', label, value, sub }) {
  return (
    <div className="bg-white p-4 rounded-2xl shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${TONE_BG[tone] || TONE_BG.gray}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 truncate">{label}</p>
          <p className="text-xl font-semibold tabular-nums">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5 truncate" title={sub}>{sub}</p>}
        </div>
      </div>
    </div>
  );
}

// Exactly the assets the sidebar uses for these platforms — keep them in sync so
// Google Ads / Meta look identical wherever they appear in the dashboard.
// (Sidebar.js: '/google_ads-512.webp' and '/6033716.png', both rounded-sm.)
const PLATFORM_ICON = {
  google: { src: '/google_ads-512.webp', alt: 'Google Ads' },
  meta: { src: '/6033716.png', alt: 'Meta Ads' },
};

function PlatformIcon({ platform, className = 'w-4 h-4' }) {
  const p = PLATFORM_ICON[platform] || PLATFORM_ICON.google;
  return <img src={p.src} alt={p.alt} className={`${className} shrink-0 rounded-sm`} />;
}

function TabButton({ active, onClick, loading, label, count, platform }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${active ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white'}`}
    >
      <PlatformIcon platform={platform} className="w-4 h-4" />
      {label}
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <span className={`text-[11px] ${active ? 'text-gray-300' : 'text-gray-400'}`} title="shown / total">
          ({count})
        </span>
      )}
    </button>
  );
}

function CheckboxFilter({ checked, onChange, label, title }) {
  return (
    <label
      className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-gray-900"
      />
      {label}
    </label>
  );
}

function AdDetailModal({ ad, platform, verdict, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const isMeta = platform === 'meta';
  const images = (ad.images && ad.images.length)
    ? ad.images
    : [ad.imageUrl, ad.thumbnailUrl].filter(Boolean);
  const status = isMeta ? (ad.effectiveStatus || ad.status) : ad.status;
  const groupLabel = isMeta ? 'Ad Set' : 'Ad Group';
  const groupName = isMeta ? ad.adsetName : ad.adGroupName;
  const groupId = isMeta ? ad.adsetId : ad.adGroupId;

  // Text blocks
  const headlines = isMeta
    ? [ad.title, ...((ad.titles || []).filter((t) => t && t !== ad.title))].filter(Boolean)
    : (ad.headlines || []);
  const bodies = isMeta
    ? [ad.body, ...((ad.bodies || []).filter((t) => t && t !== ad.body))].filter(Boolean)
    : (ad.descriptions || []);
  const links = isMeta ? [ad.linkUrl].filter(Boolean) : (ad.finalUrls || []);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <StatusBadge status={status} />
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                <PlatformIcon platform={isMeta ? 'meta' : 'google'} className="w-3.5 h-3.5" />
                {isMeta ? 'Meta' : 'Google'}{ad.adType ? ` · ${ad.adType}` : ''}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 truncate" title={ad.campaignName}>{ad.campaignName || '—'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{ad.accountName || ad.accountId}</p>
          </div>
          <button onClick={onClose} className="shrink-0 p-1.5 rounded-xl hover:bg-gray-200 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* AI verdict */}
          {verdict && (
            <div className={`rounded-xl px-4 py-3 text-sm flex items-start gap-2 ${verdict.verdict === 'inconsistent' ? 'bg-red-50 text-red-800 ring-1 ring-red-200' : verdict.verdict === 'consistent' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200' : 'bg-gray-50 text-gray-600 ring-1 ring-gray-200'}`}>
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold flex items-center gap-2">
                  AI consistency: <VerdictBadge v={verdict} />
                </div>
                {verdict.reason && <div className="mt-0.5 text-[13px]">{verdict.reason}</div>}
              </div>
            </div>
          )}

          {/* Meta info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <Field label={groupLabel} value={groupName} sub={groupId} />
            <Field label="Ad Name" value={ad.adName || '(unnamed)'} sub={ad.adId} />
            <Field label="Campaign ID" value={ad.campaignId} />
            <Field label="Impr. — Yesterday" value={fmtInt(ad.impressionsYesterday)} />
            <Field label="Impr. — Last 7d" value={fmtInt(ad.impressions7d)} />
            <Field label="Impr. — Last 30d" value={fmtInt(ad.impressions30d)} />
          </div>

          {/* Targeted keywords (Google ad group) */}
          {!isMeta && Array.isArray(ad.keywords) && ad.keywords.length > 0 && (
            <div>
              <Label>Targeted Keywords ({ad.keywords.length})</Label>
              <KeywordTable keywords={ad.keywords} />
              <p className="mt-1.5 text-[10px] text-gray-400">
                Only <b>serving</b> keywords (those with impressions) feed the AI consistency check —
                a zero-impression keyword spends nothing, so it cannot flag an inconsistency. The ad
                copy should match the search intent behind these terms.
              </p>
            </div>
          )}

          {/* Images */}
          {images.length > 0 && (
            <div>
              <Label>Images ({images.length})</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {images.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" title="Full size">
                    <img src={u} alt="" loading="lazy" className="w-28 h-28 object-cover rounded-xl ring-1 ring-gray-200 bg-gray-50 hover:ring-gray-900" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Headlines / Titles */}
          {headlines.length > 0 && (
            <div>
              <Label>{isMeta ? 'Title(s)' : 'Headlines'}</Label>
              <ul className="mt-1.5 space-y-1">
                {headlines.map((t, i) => (
                  <li key={i} className="text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-1.5 whitespace-pre-wrap break-words">{t}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Descriptions / Body */}
          {bodies.length > 0 && (
            <div>
              <Label>{isMeta ? 'Body Text' : 'Descriptions'}</Label>
              <ul className="mt-1.5 space-y-1">
                {bodies.map((t, i) => (
                  <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-1.5 whitespace-pre-wrap break-words">{t}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Extra Google fields */}
          {!isMeta && (ad.longHeadline || ad.path1 || ad.path2 || ad.businessName) && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              {ad.longHeadline && <Field label="Long Headline" value={ad.longHeadline} />}
              {(ad.path1 || ad.path2) && <Field label="Path" value={[ad.path1, ad.path2].filter(Boolean).join(' / ')} />}
              {ad.businessName && <Field label="Business Name" value={ad.businessName} />}
            </div>
          )}

          {/* CTA */}
          {isMeta && ad.callToAction && (
            <div className="text-xs"><Field label="CTA" value={ad.callToAction} /></div>
          )}

          {/* Links */}
          {links.length > 0 && (
            <div>
              <Label>{isMeta ? 'Destination Link' : 'Final URL'}</Label>
              <div className="mt-1.5 space-y-1">
                {links.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-gray-900 underline decoration-gray-300 hover:decoration-gray-900 break-all">
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {u}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Catalog products (Meta DPA) */}
          {isMeta && Array.isArray(ad.products) && ad.products.length > 0 && (
            <div>
              <Label>Catalog Products (sample of {ad.products.length})</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {ad.products.map((p, i) => (
                  <a key={i} href={p.url || '#'} target="_blank" rel="noreferrer" className="block rounded-xl ring-1 ring-gray-200 overflow-hidden hover:ring-gray-900">
                    {p.image && <img src={p.image} alt="" loading="lazy" className="w-full h-28 object-cover bg-gray-50" />}
                    <div className="p-2">
                      <div className="text-[11px] text-gray-800 line-clamp-2" title={p.name}>{p.name}</div>
                      {p.retailerId && <div className="text-[10px] text-gray-400 mt-0.5">{p.retailerId}</div>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</div>;
}

function Field({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 truncate" title={value || ''}>{value || '—'}</div>
      {sub && <div className="text-[10px] text-gray-400 truncate">{sub}</div>}
    </div>
  );
}

function AccountSummary({ accounts, platform }) {
  return (
    <div className="flex flex-wrap gap-2">
      {accounts.map((a, i) => (
        <div
          key={`${a.accountId || 'acc'}-${i}`}
          className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-xl shadow-sm ${a.error ? 'bg-red-50 text-red-700 ring-1 ring-red-200' : 'bg-white text-gray-700 border border-gray-200'}`}
          title={a.error || ''}
        >
          <PlatformIcon platform={platform} className="w-3.5 h-3.5" />
          {a.error ? <AlertCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
          {a.accountName || a.accountId || 'Account'} · {a.count ?? 0}
          {a.error && <span className="italic">— {a.error.slice(0, 60)}</span>}
        </div>
      ))}
    </div>
  );
}

function ErrorBanner({ text, onRetry, loading }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 text-sm text-red-700 flex items-start gap-3">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{text}</div>
      <button
        onClick={onRetry}
        disabled={loading}
        className="text-xs font-medium text-red-700 underline disabled:opacity-50"
      >
        Try again
      </button>
    </div>
  );
}

// Kampanya / reklam grubu / reklam TEK hücrede alt alta.
// Ayrı sütun olarak durduklarında tablo yatayda taşıyordu; Account sütunu da
// kaldırıldı (hesap adı detay panelinde ve üstteki hesap filtresinde zaten var).
function EntityCell({ campaign, campaignId, group, groupId, groupLabel, ad, adId, children }) {
  // Etiket sabit genislikte bir kolon: uzun kampanya adi ikinci satira tastiginda
  // metin etiketin altina degil, degerlerin hizasina devam ediyor.
  const line = (label, value, valueCls, tip) => (
    <div className="flex gap-2 leading-snug" title={tip || value || ''}>
      <span className="w-[66px] shrink-0 pt-[3px] text-[9px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <span className={`min-w-0 break-words ${valueCls}`}>{value || '—'}</span>
    </div>
  );
  return (
    <div className="min-w-[210px] max-w-[280px] space-y-1">
      {line('Campaign', campaign, 'text-[13px] font-medium text-gray-900', campaignId ? `${campaign || ''} · ${campaignId}` : campaign)}
      {line(groupLabel, group, 'text-xs text-gray-600', groupId ? `${group || ''} · ${groupId}` : group)}
      {line('Ad', ad || '(unnamed)', 'text-xs text-gray-600', adId ? `${ad || ''} · ${adId}` : ad)}
      {adId && <div className="pl-[74px] text-[10px] text-gray-400 pt-0.5">ID {adId}</div>}
      {children ? <div className="pl-[74px]">{children}</div> : null}
    </div>
  );
}

function GoogleTable({ ads, loading, onRowClick, verdicts = {}, checking }) {
  if (loading && !ads.length) return <TableSkeleton />;
  if (!ads.length) return <EmptyState text="No ads found" />;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <Th>Status</Th>
            <Th>Campaign / Ad Group / Ad</Th>
            <Th>Type</Th>
            <Th>Image</Th>
            <Th>Headlines</Th>
            <Th>Descriptions</Th>
            <Th>Final URL</Th>
            <Th>Delivery (Impr.)</Th>
            <Th>AI Verdict</Th>
          </tr>
        </thead>
        <tbody>
          {ads.map((a, i) => (
            <tr key={`${a.adId}-${i}`} onClick={() => onRowClick && onRowClick(a)} className="border-t border-gray-100 hover:bg-gray-50 align-top cursor-pointer">
              <Td><StatusBadge status={a.status} /></Td>
              <Td>
                <EntityCell
                  campaign={a.campaignName} campaignId={a.campaignId}
                  groupLabel="Ad group" group={a.adGroupName} groupId={a.adGroupId}
                  ad={a.adName} adId={a.adId}
                >
                  {Array.isArray(a.keywords) && a.keywords.length > 0 && (
                    <div className="pt-1.5">
                      <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Keywords ({a.keywords.length})</div>
                      <KeywordChips keywords={a.keywords} limit={5} />
                    </div>
                  )}
                </EntityCell>
              </Td>
              <Td className="whitespace-nowrap text-[10px] text-gray-500">{a.adType}</Td>
              <Td><Thumbs urls={a.images} /></Td>
              <Td>
                <TextList items={a.headlines} />
                {a.longHeadline && (
                  <div className="mt-1 text-[10px] text-gray-600"><b>Long:</b> {a.longHeadline}</div>
                )}
              </Td>
              <Td>
                <TextList items={a.descriptions} />
              </Td>
              <Td>
                {(a.finalUrls || []).map((u, j) => (
                  <a key={j} href={u} target="_blank" rel="noreferrer" className="text-gray-900 underline decoration-gray-300 hover:decoration-gray-900 block truncate max-w-[180px] text-xs" title={u}>{u}</a>
                ))}
              </Td>
              <Td><ImpressionCell ad={a} /></Td>
              <Td onClick={(e) => e.stopPropagation()}>
                <VerdictCell v={verdicts[adKey(a, 'google')]} checking={checking} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetaTable({ ads, loading, onRowClick, verdicts = {}, checking }) {
  if (loading && !ads.length) return <TableSkeleton />;
  if (!ads.length) return <EmptyState text="No ads found" />;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <Th>Status</Th>
            <Th>Campaign / Ad Set / Ad</Th>
            <Th>Image</Th>
            <Th>Title</Th>
            <Th>Body</Th>
            <Th>CTA</Th>
            <Th>Link</Th>
            <Th>Delivery (Impr.)</Th>
            <Th>AI Verdict</Th>
          </tr>
        </thead>
        <tbody>
          {ads.map((a, i) => (
            <tr key={`${a.adId}-${i}`} onClick={() => onRowClick && onRowClick(a)} className="border-t border-gray-100 hover:bg-gray-50 align-top cursor-pointer">
              <Td>
                <StatusBadge status={a.effectiveStatus || a.status} />
                {a.effectiveStatus && a.effectiveStatus !== a.status && (
                  <div className="text-[9px] text-gray-400 mt-0.5">config: {a.status}</div>
                )}
              </Td>
              <Td>
                <EntityCell
                  campaign={a.campaignName} campaignId={a.campaignId}
                  groupLabel="Ad set" group={a.adsetName} groupId={a.adsetId}
                  ad={a.adName} adId={a.adId}
                />
              </Td>
              <Td><Thumbs urls={(a.images && a.images.length) ? a.images : (a.imageUrl || a.thumbnailUrl)} /></Td>
              <Td>
                <div className="max-w-[200px] whitespace-pre-wrap break-words text-xs text-gray-700">{a.title}</div>
                {a.titles?.length > 1 && <TextList items={a.titles.slice(1)} mutedLabel="other titles" />}
              </Td>
              <Td>
                <div className="max-w-[260px] whitespace-pre-wrap break-words text-xs text-gray-700">{a.body}</div>
                {a.bodies?.length > 1 && <TextList items={a.bodies.slice(1)} mutedLabel="other bodies" />}
              </Td>
              <Td className="whitespace-nowrap text-[10px] text-gray-600">{a.callToAction || '—'}</Td>
              <Td>
                {a.linkUrl ? (
                  <a href={a.linkUrl} target="_blank" rel="noreferrer" className="text-gray-900 underline decoration-gray-300 hover:decoration-gray-900 block truncate max-w-[180px] text-xs" title={a.linkUrl}>{a.linkUrl}</a>
                ) : '—'}
              </Td>
              <Td><ImpressionCell ad={a} /></Td>
              <Td onClick={(e) => e.stopPropagation()}>
                <VerdictCell v={verdicts[adKey(a, 'meta')]} checking={checking} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Thumbs({ urls }) {
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  if (!list.length) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1 max-w-[120px]">
      {list.slice(0, 4).map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noreferrer" title="Click for full size">
          <img
            src={u}
            alt=""
            loading="lazy"
            className="w-12 h-12 object-cover rounded-lg ring-1 ring-gray-200 bg-gray-50 hover:ring-gray-900"
          />
        </a>
      ))}
      {list.length > 4 && (
        <span className="text-[10px] text-gray-400 self-center">+{list.length - 4}</span>
      )}
    </div>
  );
}

function TextList({ items, mutedLabel }) {
  if (!items || !items.length) return <span className="text-gray-300">—</span>;
  return (
    <div className="max-w-[220px] space-y-0.5">
      {mutedLabel && (
        <div className="text-[10px] text-gray-400">{mutedLabel}:</div>
      )}
      {items.map((t, i) => (
        <div key={i} className="text-[11px] leading-snug truncate text-gray-700" title={t}>{t}</div>
      ))}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-2.5 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
      {children}
    </th>
  );
}
function Td({ children, className = '', onClick }) {
  return <td className={`px-4 py-3 align-top ${className}`} onClick={onClick}>{children}</td>;
}

function TableSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-10 flex flex-col items-center gap-3 text-gray-400">
      <Loader2 className="w-7 h-7 animate-spin text-gray-500" />
      <p className="text-sm">Fetching ads…</p>
      <p className="text-[11px]">May take 20–90 seconds depending on the number of accounts.</p>
    </div>
  );
}
function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-sm text-gray-500">
      {text}
    </div>
  );
}
